import { useState } from 'react'
import type { CSSProperties, JSX } from 'react'

import {
  Button,
  CopyIcon,
  Heading,
  Link,
  Tabs,
  Text,
  Tooltip,
} from '@buffer-mono/popcorn'

import { getEntriesWithKeys } from '~publish/helpers/getEntriesWithKeys'

import { GenerateTokenDialog } from '../../../DeveloperDashboard/components/GenerateTokenDialog'
import {
  useGeneratePersonalAccessToken,
  usePersonalAccessTokens,
} from '../../../DeveloperDashboard/hooks'
import { CopyButton } from '../../../shared/components/CopyButton'
import { useIntegrationConnections } from '../../../shared/hooks/useIntegrationConnections'
import { isTokenExpired } from '../../../shared/utils/tokenExpiration'
import {
  getMcpServerUrl,
  injectApiKeyIntoSteps,
  injectMcpUrlIntoSteps,
} from '../../helpers'
import { INTEGRATION_CONFIG } from '../../integrationConfig'
import type {
  SetupStep,
  SetupStepItem as SetupStepItemType,
} from '../../integrationConfig'
import type { Integration } from '../../../shared/integrations/types'
import { ApiKeySection } from '../ApiKeySection'
import { ConnectedSection } from '../ConnectedSection'

import styles from './IntegrationDetailPage.module.css'

const EXPIRED_TOOLTIP =
  'This API key has expired. Regenerate it or create a new one.'

function getSetupStepItemKey(item: SetupStepItemType): string {
  return typeof item === 'string' ? item : (item.displayText ?? item.text)
}

function getSetupStepKey(step: SetupStep): string {
  return step.title
}

function DisabledCopyButton({
  label,
  className,
}: {
  label: string
  className?: string
}): JSX.Element {
  return (
    <Tooltip content={EXPIRED_TOOLTIP}>
      <span
        className={className}
        tabIndex={0}
        // The focusable proxy exposes the disabled copy action's explanation;
        // nesting a semantic button around the disabled button would be invalid.
        // eslint-disable-next-line jsx-a11y/prefer-tag-over-role
        role="button"
        aria-disabled="true"
        aria-label={label}
      >
        <Button variant="tertiary" disabled aria-label={label}>
          <CopyIcon />
        </Button>
      </span>
    </Tooltip>
  )
}

function SetupStepListItem({
  item,
  isExpired,
}: {
  item: SetupStepItemType
  isExpired: boolean
}): JSX.Element {
  if (typeof item === 'string') {
    return (
      <Text as="span" color="subtle">
        {item}
      </Text>
    )
  }

  return (
    <span className={styles.copyableItem}>
      <Text as="span" color="subtle">
        {item.text}
      </Text>
      {item.copyText && (
        <>
          <code className={styles.copyableItemText}>
            {item.displayText ?? item.copyText}
          </code>
          {isExpired ? (
            <DisabledCopyButton label="Copy (expired)" />
          ) : (
            <CopyButton textToCopy={item.copyText} label="Copy" />
          )}
        </>
      )}
    </span>
  )
}

type IntegrationIconStyle = CSSProperties & {
  '--integration-icon-bg': string
}

const getIntegrationIconStyle = (
  iconBgColor: string,
): IntegrationIconStyle => ({
  '--integration-icon-bg': iconBgColor,
})

export function IntegrationIcon({
  integration,
}: {
  integration: Integration
}): JSX.Element {
  return (
    <div
      className={styles.headerIcon}
      style={
        integration.iconSrc
          ? undefined
          : getIntegrationIconStyle(integration.iconBgColor)
      }
      aria-hidden="true"
    >
      {integration.iconSrc ? (
        <img
          src={integration.iconSrc}
          alt=""
          className={styles.headerIconImage}
        />
      ) : (
        integration.iconLetter
      )}
    </div>
  )
}

function SetupStepItem({
  step,
  stepNumber,
  isExpired,
}: {
  step: SetupStep
  stepNumber: number
  isExpired: boolean
}): JSX.Element {
  const [selectedOs, setSelectedOs] = useState<'mac' | 'windows'>(() =>
    navigator.userAgent.includes('Windows') ? 'windows' : 'mac',
  )

  const renderCodePanel = (os: 'mac' | 'windows'): JSX.Element => (
    <div className={styles.codePanel}>
      <pre className={styles.codeBlock}>
        <code>
          {step.windowsCode && os === 'windows' ? step.windowsCode : step.code}
        </code>
      </pre>
      {isExpired ? (
        <DisabledCopyButton
          label="Copy code (expired)"
          className={styles.copyCodeButton}
        />
      ) : (
        <CopyButton
          textToCopy={
            step.windowsCode && os === 'windows'
              ? (step.windowsCopyCode ?? step.windowsCode)
              : (step.copyCode ?? step.code ?? '')
          }
          label="Copy code"
          className={styles.copyCodeButton}
        />
      )}
    </div>
  )

  return (
    <div className={styles.step}>
      <div className={styles.stepNumber}>{stepNumber}</div>
      <div className={styles.stepContent}>
        <Text weight="medium">{step.title}</Text>
        {step.description && (
          <Text as="p" color="subtle">
            {step.description}
            {step.link && (
              <>
                {' '}
                <Link href={step.link.url} external>
                  {step.link.text}
                </Link>
              </>
            )}
          </Text>
        )}
        {step.items && (
          <ul className={styles.stepList}>
            {getEntriesWithKeys(step.items, getSetupStepItemKey).map(
              ({ key, item }) => (
                <li key={key}>
                  <SetupStepListItem item={item} isExpired={isExpired} />
                </li>
              ),
            )}
          </ul>
        )}
        {(step.code || step.windowsCode) && (
          <div className={styles.codeCard}>
            {step.windowsCode ? (
              <Tabs
                value={selectedOs}
                onChange={(value): void =>
                  setSelectedOs(value === 'windows' ? 'windows' : 'mac')
                }
              >
                <Tabs.TabList>
                  <Tabs.Tab value="mac">Mac</Tabs.Tab>
                  <Tabs.Tab value="windows">Windows</Tabs.Tab>
                </Tabs.TabList>
                <Tabs.Panel value="mac" className={styles.codeTabPanel}>
                  {renderCodePanel('mac')}
                </Tabs.Panel>
                <Tabs.Panel value="windows" className={styles.codeTabPanel}>
                  {renderCodePanel('windows')}
                </Tabs.Panel>
              </Tabs>
            ) : (
              renderCodePanel('mac')
            )}
          </div>
        )}
        {step.note && (
          <Text as="p" color="subtle" className={styles.note}>
            <strong>Note:</strong> {step.note}
          </Text>
        )}
      </div>
    </div>
  )
}

interface IntegrationDetailPageProps {
  integration: Integration
}

export const IntegrationDetailPage = ({
  integration,
}: IntegrationDetailPageProps): JSX.Element => {
  const {
    tokens,
    isLoading: isLoadingToken,
    refetch,
  } = usePersonalAccessTokens()
  const { generateToken, isLoading: isGenerating } =
    useGeneratePersonalAccessToken()
  const connectionsByIntegration = useIntegrationConnections()
  const [isGenerateDialogOpen, setIsGenerateDialogOpen] = useState(false)
  const [selectedTokenId, setSelectedTokenId] = useState<string | null>(null)

  const selectedToken =
    tokens.find((token) => token.accessToken === selectedTokenId) ??
    tokens[0] ??
    null
  const config = INTEGRATION_CONFIG[integration.id]
  const requiresApiKey = config.requiresApiKey !== false
  const stepOffset = requiresApiKey ? 2 : 1
  const isSelectedTokenExpired =
    requiresApiKey && selectedToken
      ? isTokenExpired(selectedToken.expiresAt)
      : false
  const apiKey =
    selectedToken && !isSelectedTokenExpired ? selectedToken.accessToken : null
  const mcpUrl = getMcpServerUrl()

  const [selectedMethod, setSelectedMethod] = useState(
    config.setupMethods?.[0]?.id ?? '',
  )

  return (
    <>
      <div className={styles.main}>
        <Text as="p">{config.description}</Text>
        {config.exampleUseCases && <Text as="p">{config.exampleUseCases}</Text>}

        <ConnectedSection
          connections={connectionsByIntegration.get(integration.id) ?? []}
        />

        {config.setupMethods && config.setupMethods.length > 1 ? (
          <Tabs
            defaultValue={config.setupMethods[0]?.id}
            value={selectedMethod}
            onChange={(value): void => setSelectedMethod(value)}
            className={styles.methodTabs}
          >
            <Tabs.TabList>
              {config.setupMethods.map((method) => (
                <Tabs.Tab key={method.id} value={method.id}>
                  {method.label}
                </Tabs.Tab>
              ))}
            </Tabs.TabList>
            {config.setupMethods.map((method) => (
              <Tabs.Panel key={method.id} value={method.id}>
                <div className={styles.steps}>
                  {requiresApiKey && (
                    <div className={styles.step}>
                      <div className={styles.stepNumber}>1</div>
                      <div className={styles.stepContent}>
                        <Text weight="medium">
                          Create an API Key or Copy an Existing One
                        </Text>
                        <Text as="p" color="subtle">
                          You need an API key to integrate Buffer with{' '}
                          {integration.name}. Use your existing key below or
                          create a new one.
                        </Text>

                        <ApiKeySection
                          integrationName={integration.name}
                          tokens={tokens}
                          isLoading={isLoadingToken}
                          selectedToken={selectedToken}
                          onSelectToken={(token): void =>
                            setSelectedTokenId(token.accessToken)
                          }
                          onGenerateClick={() => setIsGenerateDialogOpen(true)}
                        />
                      </div>
                    </div>
                  )}
                  {getEntriesWithKeys(
                    injectMcpUrlIntoSteps(
                      injectApiKeyIntoSteps(method.steps, apiKey),
                      mcpUrl,
                    ),
                    getSetupStepKey,
                  ).map(({ key, item: step }, index) => (
                    <SetupStepItem
                      key={key}
                      step={step}
                      stepNumber={index + stepOffset}
                      isExpired={isSelectedTokenExpired}
                    />
                  ))}
                </div>
              </Tabs.Panel>
            ))}
          </Tabs>
        ) : (
          <section className={styles.section}>
            <Heading as="h2" size="small">
              Setup
            </Heading>

            {requiresApiKey && (
              <div className={styles.step}>
                <div className={styles.stepNumber}>1</div>
                <div className={styles.stepContent}>
                  <Text weight="medium">
                    Create an API Key or Copy an Existing One
                  </Text>
                  <Text as="p" color="subtle">
                    You need an API key to integrate Buffer with{' '}
                    {integration.name}. Use your existing key below or create a
                    new one.
                  </Text>

                  <ApiKeySection
                    integrationName={integration.name}
                    tokens={tokens}
                    isLoading={isLoadingToken}
                    selectedToken={selectedToken}
                    onSelectToken={(token): void =>
                      setSelectedTokenId(token.accessToken)
                    }
                    onGenerateClick={() => setIsGenerateDialogOpen(true)}
                  />
                </div>
              </div>
            )}

            {config.setupMethods && config.setupMethods.length === 1 ? (
              getEntriesWithKeys(
                injectMcpUrlIntoSteps(
                  injectApiKeyIntoSteps(config.setupMethods[0].steps, apiKey),
                  mcpUrl,
                ),
                getSetupStepKey,
              ).map(({ key, item: step }, index) => (
                <SetupStepItem
                  key={key}
                  step={step}
                  stepNumber={index + stepOffset}
                  isExpired={isSelectedTokenExpired}
                />
              ))
            ) : (
              <div className={styles.step}>
                <div className={styles.stepNumber}>2</div>
                <div className={styles.stepContent}>
                  <Text weight="medium">Connect in {integration.name}</Text>
                  <Text as="p" color="subtle">
                    Open {integration.name}, select Buffer, and paste the API
                    key in the window that opens.
                  </Text>
                </div>
              </div>
            )}
          </section>
        )}

        {config.examplePrompts && config.examplePrompts.length > 0 && (
          <section className={styles.section}>
            <Heading as="h2" size="small">
              Try It Out
            </Heading>
            <Text as="p">
              Copy any of these example prompts to get started with{' '}
              {integration.name}:
            </Text>
            <div className={styles.examplePrompts}>
              {getEntriesWithKeys(
                config.examplePrompts,
                (prompt) => prompt,
              ).map(({ key, item: prompt }) => (
                <div key={key} className={styles.examplePrompt}>
                  <Text as="p" className={styles.examplePromptText}>
                    {prompt}
                  </Text>
                  <CopyButton
                    textToCopy={prompt}
                    label="Copy prompt"
                    className={styles.examplePromptCopy}
                  />
                </div>
              ))}
            </div>
            <Text as="p" color="subtle" size="sm">
              <strong>Disclaimer:</strong> This integration uses large language
              models (LLMs) to generate responses. Responses may occasionally be
              inaccurate or incomplete. Always verify important details before
              taking action.
            </Text>
          </section>
        )}
      </div>

      <GenerateTokenDialog
        open={isGenerateDialogOpen}
        onOpenChange={setIsGenerateDialogOpen}
        onGenerate={generateToken}
        onSuccess={(): void => {
          void refetch()
        }}
        isLoading={isGenerating}
      />
    </>
  )
}
